import axios from 'axios';
import { config } from './config';
import { userManager } from './auth';
import type { CheckResponse, Monitor, MonitorEvent, MonitorInput, Settings } from './types';

export const http = axios.create({ baseURL: config.apiUrl, timeout: 60_000 });
http.interceptors.request.use(async request => {
  const user = await userManager?.getUser();
  if (!user || user.expired) { await userManager?.removeUser(); throw new Error('Your session has expired. Sign in again.'); }
  request.headers.Authorization = `Bearer ${user.access_token}`;
  return request;
});
http.interceptors.response.use(response => response, async error => {
  if (axios.isAxiosError(error) && error.response?.status === 401) await userManager?.removeUser();
  return Promise.reject(error);
});
export function errorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return 'Your session has expired. Sign in again.';
    if (typeof error.response?.data?.message === 'string') return error.response.data.message;
    if (!error.response) return 'The API could not be reached. Check your connection and API/CORS configuration. The action may have reached the server; refresh before trying again.';
    return `Request failed (HTTP ${error.response.status}). Please try again.`;
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
const path = (id: string) => `/monitors/${encodeURIComponent(id)}`;
const idempotency = (key: string) => ({ headers: { 'Idempotency-Key': key } });
export const api = {
  monitors: async (signal?: AbortSignal) => (await http.get<{ monitors: Monitor[] }>('/monitors', { signal })).data.monitors,
  monitor: async (id: string, signal?: AbortSignal) => (await http.get<Monitor>(path(id), { signal })).data,
  create: async (input: MonitorInput) => (await http.post<Monitor>('/monitors', input)).data,
  update: async (id: string, input: Partial<MonitorInput> & { enabled?: boolean }) => (await http.patch<Monitor>(path(id), input)).data,
  remove: async (id: string) => (await http.delete<{ ok: boolean }>(path(id))).data,
  check: async (id: string, key: string) => (await http.post<CheckResponse>(`${path(id)}/check`, {}, idempotency(key))).data,
  events: async (id: string, signal?: AbortSignal) => (await http.get<{ events: MonitorEvent[] }>(`${path(id)}/events`, { signal })).data.events,
  report: async (key: string) => (await http.post<{ emailStatus: unknown }>('/reports/email', {}, idempotency(key))).data,
  settings: async (signal?: AbortSignal) => (await http.get<Settings>('/settings', { signal })).data,
  saveSettings: async (input: Settings) => (await http.patch<Settings>('/settings', input)).data,
};
